/**
 * Profile — your details, your password, what Leadgible emails you, and the door out.
 *
 * Visual Reference: `design/screens/settings-profile.html` — states default,
 * **saved**, error, **leave**, loading. Its own notes are the brief:
 *
 *   · "The strength indicator describes what to do next instead of scoring the
 *     password out of five."
 *   · "the toast uses the same verb as the button that caused it: Save changes,
 *     then Profile saved."
 *   · "the confirmation says what is lost, what is kept, and that being invited
 *     back is possible. Destructive button, calm copy."
 *
 * PRD §7.6 makes every route here `All (self)`, and that is structural: the
 * user id comes from the session cookie, so nothing on this page can name
 * anybody else.
 *
 * THE AVATAR IS INITIALS ONLY. The mockup draws a "Change" button beside it;
 * the story says upload is out of the MVP set because PRD §4.3 places no
 * storage for it, and `docs/BACKLOG.md` records it. The helper line the mockup
 * pairs with the button goes with it.
 *
 * The two-factor card sits between Password and "What we email you", where the
 * mockup draws it. Story 4.4 builds it; this page owns its placement and the
 * toast it announces through.
 *
 * Story 4.3.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPatch, apiPost, apiPut, ApiFailure } from '../../lib/api'
import { useMe } from '../../shell/useMe'
import { Avatar } from '../../ui/Avatar'
import { Button } from '../../ui/Button'
import { Field } from '../../ui/Field'
import { Modal } from '../../ui/Modal'
import { PasswordChecks } from '../../ui/PasswordChecks'
import { Skeleton } from '../../ui/Skeleton'
import { TwoFactorCard } from './TwoFactor'

type Preferences = { hotLead: boolean; digest: boolean; attention: boolean }

/** The three toggles, with the mockup's copy for each. */
const NOTIFICATIONS: { key: keyof Preferences; label: string; detail: string }[] = [
  {
    key: 'hotLead',
    label: 'Email me when a hot lead arrives',
    detail:
      'Straight away. The subject line is the verdict, so you can judge it without opening anything.',
  },
  {
    key: 'digest',
    label: 'Email me the daily digest',
    detail: 'One message each morning with everything that came in.',
  },
  {
    key: 'attention',
    label: 'Email me when something needs attention',
    detail: 'A failed send, a funnel near its limit, a key that stopped working.',
  },
]

export function Profile() {
  const me = useMe()
  const [prefs, setPrefs] = useState<Preferences | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  /**
   * Mirrored locally so the card flips the moment it changes.
   *
   * The truth is `user.twoFactorEnabled` on the boot call — the contract has no
   * `GET /v1/me/2fa` (Story 4.4's route comment explains why) — and a reload
   * re-reads it. Turning it on rotates the session, so a stale card would be
   * the only thing on the page still claiming it is off.
   */
  const [twoFactor, setTwoFactor] = useState(false)

  // Seed the card from the boot call the first time it lands.
  const bootTwoFactor = me.status === 'ready' ? Boolean(me.me.user.twoFactorEnabled) : false
  useEffect(() => {
    setTwoFactor(bootTwoFactor)
  }, [bootTwoFactor])

  const load = useCallback(async () => {
    try {
      setPrefs(await apiGet<Preferences>('/v1/me/notifications'))
    } catch {
      // A preference that will not load is not a reason to withhold the page —
      // the defaults are what a person who has never opened it already has.
      setPrefs({ hotLead: true, digest: true, attention: true })
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /** The toast the mockup's `saved` frame draws, with the button's own verb. */
  const announce = useCallback((message: string) => {
    setToast(message)
    const timer = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(timer)
  }, [])

  const heading = (
    <h1 className="m-0 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">Profile</h1>
  )

  if (me.status !== 'ready' || !prefs) {
    return (
      <>
        {heading}
        <Card title="Your details">
          <Skeleton className="h-mdx-input w-full" />
          <Skeleton className="h-mdx-input w-full" />
        </Card>
        <Card title="Password">
          <Skeleton className="h-mdx-input w-full" />
          <Skeleton className="h-mdx-input w-full" />
        </Card>
      </>
    )
  }

  const user = me.me.user
  const org = me.me.orgs[0]

  return (
    <>
      {heading}
      <DetailsCard user={user} onSaved={() => announce('Profile saved')} />
      <PasswordCard onSaved={() => announce('Password updated')} />
      <TwoFactorCard
        enabled={twoFactor}
        onChanged={(message, enabled) => {
          setTwoFactor(enabled)
          announce(message)
        }}
      />
      <NotificationsCard
        prefs={prefs}
        onChange={(next) => {
          setPrefs(next)
          void apiPut('/v1/me/notifications', next)
        }}
      />
      {org ? <LeaveCard org={org} /> : null}

      {/**
       * "The toast uses the same verb as the button that caused it."
       *
       * `role="status"` rather than `alert`: it confirms something that went
       * right, and a screen reader should hear it without being interrupted.
       */}
      {toast ? (
        <div
          role="status"
          className="fixed bottom-mdx-24 left-1/2 z-40 flex -translate-x-1/2 items-center gap-mdx-8 rounded-mdx-control bg-mdx-ink px-mdx-16 py-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-white shadow-mdx-card"
        >
          <span
            aria-hidden="true"
            className="flex size-mdx-20 items-center justify-center rounded-mdx-pill bg-mdx-hot text-mdx-11 font-mdx-bold"
          >
            ✓
          </span>
          {toast}
        </div>
      ) : null}
    </>
  )
}

/* ── the cards ──────────────────────────────────────────────────────────── */

/** §2 Card, with the 17px/600 title the mockup puts above each section. */
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex max-w-mdx-measure-60 flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      <h2 className="m-0 text-mdx-17 font-mdx-semibold tracking-mdx-h3">{title}</h2>
      {children}
    </section>
  )
}

function DetailsCard({
  user,
  onSaved,
}: {
  user: { name: string; email: string; emailVerified: boolean }
  onSaved: () => void
}) {
  const [name, setName] = useState(user.name)
  const [email, setEmail] = useState(user.email)
  const [verified, setVerified] = useState(user.emailVerified)
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const saved = await apiPatch<{ emailVerified: boolean }>('/v1/me', { name, email })
      setVerified(saved.emailVerified)
      onSaved()
    } catch (failure) {
      // "Error — inline and per-field. The message names the actual conflict
      // rather than saying the email is invalid." — the mockup's own note.
      setError(
        failure instanceof ApiFailure
          ? { field: failure.details?.['field'] as string | undefined, message: failure.message }
          : { message: 'Something went wrong.' },
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card title="Your details">
      <div className="flex items-center gap-mdx-16">
        {/* Initials only — upload is out of the MVP set (docs/BACKLOG.md). */}
        <Avatar name={user.name} size="xl" />
        <span className="max-w-mdx-measure-44 text-mdx-13 text-mdx-slate">
          Your initials stand in for a picture. Everyone in your organisation sees the same ones.
        </span>
      </div>

      <Field
        label="Name"
        value={name}
        maxLength={80}
        autoComplete="name"
        error={error?.field === 'name' ? error.message : undefined}
        onChange={(event) => setName(event.target.value)}
      />

      {/**
        * "✓ Verified" sits INSIDE the field, right-aligned — the mockup puts it
        * at `right: 12px; top: 9px` and gives the input `--mdx-field-badge` of
        * right padding to clear it. A badge beneath the field would read as a
        * separate fact; inside, it is plainly about this address.
        */}
      <div className="relative flex flex-col">
        <Field
          label="Email"
          type="email"
          value={email}
          autoComplete="email"
          className={verified ? 'pr-mdx-field-badge' : undefined}
          error={error?.field === 'email' ? error.message : undefined}
          helper={
            verified ? undefined : 'Not verified yet — check your inbox for the confirmation link.'
          }
          onChange={(event) => setEmail(event.target.value)}
        />
        {verified ? (
          <span
            aria-label="Verified"
            className="pointer-events-none absolute right-mdx-12 top-mdx-40 inline-flex items-center gap-mdx-6 text-mdx-13 font-mdx-semibold text-mdx-hot-ink"
          >
            <span
              aria-hidden="true"
              className="flex size-mdx-20 items-center justify-center rounded-mdx-pill bg-mdx-hot text-mdx-11 font-mdx-bold text-mdx-white"
            >
              ✓
            </span>
            Verified
          </span>
        ) : null}
      </div>

      {error && !error.field ? (
        <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
          {error.message}
        </p>
      ) : null}

      <Button
        className="self-start"
        loading={saving ? 'Saving' : undefined}
        onClick={() => void save()}
      >
        Save changes
      </Button>
    </Card>
  )
}

function PasswordCard({ onSaved }: { onSaved: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const mismatch = confirm.length > 0 && confirm !== next

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await apiPost('/v1/me/password', { currentPassword: current, newPassword: next })
      setCurrent('')
      setNext('')
      setConfirm('')
      onSaved()
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card title="Password">
      {/* The contract requires the current one, and it is what stops a borrowed
          session from becoming a permanent one. */}
      <Field
        label="Current password"
        type="password"
        value={current}
        autoComplete="current-password"
        onChange={(event) => setCurrent(event.target.value)}
      />

      <div className="flex flex-col gap-mdx-6">
        <Field
          label="New password"
          type="password"
          value={next}
          autoComplete="new-password"
          onChange={(event) => setNext(event.target.value)}
        />
        <PasswordChecks password={next} />
      </div>

      <Field
        label="Confirm new password"
        type="password"
        value={confirm}
        autoComplete="new-password"
        error={mismatch ? "Those two don't match yet." : undefined}
        helper="Three or four unrelated words beat one clever substitution."
        onChange={(event) => setConfirm(event.target.value)}
      />

      {error ? (
        <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
          {error}
        </p>
      ) : null}

      <Button
        className="self-start"
        disabled={current.length === 0 || next.length === 0 || mismatch}
        loading={saving ? 'Updating' : undefined}
        onClick={() => void save()}
      >
        Update password
      </Button>
    </Card>
  )
}

/**
 * Each preference is a TINTED ROW, not a bare checkbox.
 *
 * The mockup draws them as `--mdx-ground` blocks at radius 10 and `12px 16px`,
 * with a 20px box on a 14px gap and the label over its explanation. The whole
 * row is the `<label>`, so the hit area is the block rather than the box —
 * which is what makes the detail line clickable too.
 *
 * `ui/Checkbox` is the 18px inline control the auth screens use and it does not
 * carry a container; giving it one would change every existing use. This is the
 * row, built here, using the same native input for the same reason its header
 * gives — `accent-color` keeps the platform's keyboard behaviour and focus ring.
 */
function NotificationsCard({
  prefs,
  onChange,
}: {
  prefs: Preferences
  onChange: (next: Preferences) => void
}) {
  return (
    <Card title="What we email you">
      <div className="flex flex-col gap-mdx-8">
        {NOTIFICATIONS.map((item) => (
          <label
            key={item.key}
            className="flex cursor-pointer items-start gap-mdx-14 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12"
          >
            <input
              type="checkbox"
              checked={prefs[item.key]}
              onChange={(event) => onChange({ ...prefs, [item.key]: event.target.checked })}
              className="mt-mdx-2 size-mdx-20 shrink-0 cursor-pointer accent-mdx-brand"
            />
            <span className="flex min-w-0 flex-col">
              <span className="text-mdx-15 font-mdx-semibold">{item.label}</span>
              <span className="text-mdx-13 text-mdx-slate">{item.detail}</span>
            </span>
          </label>
        ))}
      </div>
    </Card>
  )
}

/**
 * The quiet danger zone.
 *
 * The card's border is `--mdx-line-strong` rather than `--mdx-error`: the
 * mockup's note calls it "a quiet danger zone", and a red box around something
 * a person might legitimately do reads as an alarm rather than a boundary. The
 * BUTTON is destructive; the container is not.
 */
function LeaveCard({ org }: { org: { id: string; name: string } }) {
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [leaving, setLeaving] = useState(false)

  const leave = async () => {
    setLeaving(true)
    setError(null)
    try {
      await apiPost('/v1/me/leave-org', { orgId: org.id })
      // Their grants are gone, so the shell has nothing to render. Reload into
      // whatever they still have — another organisation, or the login page.
      window.location.assign('/')
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
      setLeaving(false)
    }
  }

  return (
    <>
      {/**
        * A ROW, not a titled card — the mockup gives the danger zone the same
        * ordinary `--mdx-line` border as every other card and puts the whole
        * thing on one line. Its note calls it "a quiet danger zone", and a red
        * box around something a person may legitimately do reads as an alarm.
        */}
      <section className="flex max-w-mdx-measure-60 flex-wrap items-center justify-between gap-x-mdx-24 gap-y-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
        <span className="flex min-w-0 flex-1 basis-mdx-header-column flex-col">
          <h2 className="m-0 text-mdx-15 font-mdx-semibold">Leave {org.name}</h2>
          <span className="text-mdx-13 text-mdx-slate">
            You'll lose access to its leads and funnels. Someone with an admin role can invite you
            back.
          </span>
        </span>
        <Button variant="danger-outline" onClick={() => setConfirming(true)}>
          Leave this organisation
        </Button>
      </section>

      {confirming ? (
        <Modal
          title={`Leave ${org.name}?`}
          onClose={() => setConfirming(false)}
          actions={
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Stay
              </Button>
              <Button
                variant="danger"
                loading={leaving ? 'Leaving' : undefined}
                onClick={() => void leave()}
              >
                Leave this organisation
              </Button>
            </>
          }
        >
          <p className="m-0 max-w-mdx-measure-56 text-mdx-16 leading-mdx-body">
            You'll lose access to its workspaces, their funnels and their leads as soon as you
            confirm. <span className="font-mdx-semibold">Nothing you built is deleted</span> — the
            funnels stay with the organisation.
          </p>
          <p className="m-0 max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">
            You keep your Leadgible account and can be invited back at any time.
          </p>
          {error ? (
            <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
              {error}
            </p>
          ) : null}
        </Modal>
      ) : null}
    </>
  )
}
